import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { TfiLocationPin } from "react-icons/tfi";
import ApiService from "../nhrc_apis/ApiService";

const { EventAPI, RegistrationAPI } = ApiService;

function KnTotalApplication() {
  const navigate = useNavigate();

  /* -------------------- STATE -------------------- */
  const [jobs, setJobs] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [appCounts, setAppCounts] = useState({});

  const pageSize = 4;

  /* -------------------- FETCH EVENTS -------------------- */
  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await EventAPI.getAdminEvents();
      console.log("FULL RAW RESPONSE:", JSON.stringify(response, null, 2));

      const events =
        response?.data?.data ||
        response?.data?.content ||
        response?.data?.events ||
        response?.data?.result ||
        response?.data?.results ||
        response?.data ||
        response?.content ||
        response?.result ||
        response ||
        [];

      console.log("FIRST ITEM SAMPLE:", Array.isArray(events) ? events[0] : "N/A");

      // Filter only Knowledge Sharing events
      const knowledgeSharingEvents = Array.isArray(events)
        ? events.filter((event) => {
            const category =
              event?.program_category ||
              event?.programCategory ||
              event?.programType ||
              event?.programtype ||
              event?.eventType ||
              event?.eventtype ||
              event?.category ||
              event?.type ||
              "";

            const categoryStr = String(category).toLowerCase().trim();

            return (
              categoryStr === "knowledge sharing" ||
              categoryStr.includes("knowledge")
            );
          })
        : [];

      console.log("FILTERED KNOWLEDGE EVENTS:", knowledgeSharingEvents);
      setJobs(knowledgeSharingEvents);

      fetchApplicationCounts(knowledgeSharingEvents);
    } catch (error) {
      console.error("Error loading events:", error);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  /* -------------------- FETCH APPLICATION COUNTS -------------------- */
  const fetchApplicationCounts = async (events) => {
    const result = {};

    await Promise.all(
      events.map(async (event) => {
        try {
          const res = await RegistrationAPI.getEventRegistrations(event.id);

          const list =
            res?.data?.data ||
            res?.data?.registrations ||
            res?.data ||
            [];

          const safeList = Array.isArray(list) ? list : [];

          result[event.id] = {
            total: safeList.length,
            students: safeList.filter(
              (x) => (x?.member_type || "").toLowerCase() === "student"
            ).length,
            employees: safeList.filter(
              (x) => (x?.member_type || "").toLowerCase() === "hr"
            ).length,
            representatives: safeList.filter(
              (x) => (x?.member_type || "").toLowerCase() === "representative"
            ).length,
          };
        } catch {
          result[event.id] = {
            total: 0,
            students: 0,
            employees: 0,
            representatives: 0,
          };
        }
      })
    );

    setAppCounts(result);
  };

  /* -------------------- HELPERS -------------------- */
  const formatDate = (dateStr) => {
    const date = dateStr ? new Date(dateStr) : new Date();
    const isValid = !isNaN(date.getTime());
    return (isValid ? date : new Date()).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getField = (obj, ...keys) => {
    for (const key of keys) {
      if (obj?.[key] !== undefined && obj?.[key] !== null && obj?.[key] !== "")
        return obj[key];
    }
    return "0";
  };

  /* -------------------- FILTER -------------------- */
  const filteredJobs = useMemo(() => {
    const q = searchInput.toLowerCase();
    return jobs.filter((job) => {
      const title = (job?.title || job?.event_title || job?.eventTitle || "").toLowerCase();
      const location = (job?.location || job?.venue || job?.event_location || "").toLowerCase();
      return title.includes(q) || location.includes(q);
    });
  }, [jobs, searchInput]);

  /* -------------------- PAGINATION -------------------- */
  const totalPages = Math.ceil(filteredJobs.length / pageSize);

  const currentJobs = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return filteredJobs.slice(startIndex, startIndex + pageSize);
  }, [filteredJobs, page]);

  /* -------------------- SELECT ALL -------------------- */
  const allCurrentSelected =
    currentJobs.length > 0 &&
    currentJobs.every((job) => selectedIds.includes(job.id));

  const handleToggleSelectAll = (e) => {
    const currentIds = currentJobs.map((job) => job.id);
    if (e.target.checked) {
      setSelectedIds((prev) => [...new Set([...prev, ...currentIds])]);
    } else {
      setSelectedIds((prev) => prev.filter((id) => !currentIds.includes(id)));
    }
  };

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSearch = () => setPage(1);

  /* -------------------- DELETE -------------------- */
  const handleDelete = async () => {
    if (selectedIds.length === 0) {
      alert("Please select item(s) to delete");
      return;
    }
    if (!window.confirm(`Delete ${selectedIds.length} event(s)?`)) return;

    try {
      await Promise.all(selectedIds.map((id) => EventAPI.deleteEvent(id)));
      await fetchEvents();
    } catch (error) {
      console.error("Delete failed:", error);
      alert("Failed to delete selected events. Please try again.");
    }

    setSelectedIds([]);
    setPage(1);
  };

  /* -------------------- NAVIGATION -------------------- */
  const handleAddJob = () => navigate("/dashboard/KnowledgeSharing");

  const handlePreview = (job) =>
    navigate("/dashboard/KnowledgeSharing", { state: { job } });

  const handleViewResponses = (job) =>
    navigate(`/dashboard/KnTotalApplicationRes/${job.id}`, {
      state: {
        jobTitle: job?.title || job?.event_title || job?.eventTitle,
        jobId: job.id,
      },
    });

  /* -------------------- UI -------------------- */
  return (
    <section className="Hrjobpost">
      <h3 className="training-title mt-1 fw-bold" style={{ color: "#8D0000" }}>
        KNOWLEDGE SHARING
      </h3>

      <div className="container mt-5">
        {/* HEADER */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="mb-0">All Events</h5>
            <small className="text-muted">
              Total: {filteredJobs.length} of {jobs.length}
            </small>
          </div>
          <button
            className="btn btn-success btn-sm d-flex align-items-center gap-1 p-2"
            onClick={handleAddJob}
          >
            <i className="bi bi-plus-lg"></i> + Post Event
          </button>
        </div>

        {/* ACTION BAR */}
        <div className="bg-light border rounded-3 p-3 mb-4 d-flex flex-wrap align-items-center gap-3">
          <div className="form-check ms-1">
            <input
              type="checkbox"
              style={{ transform: "scale(0.8)", accentColor: "#0d6efd" }}
              className="form-check-input"
              id="selectAll"
              checked={allCurrentSelected}
              onChange={handleToggleSelectAll}
            />
            <label
              className="form-check-label ms-1"
              htmlFor="selectAll"
              style={{ color: "#333" }}
            >
              Select All
            </label>
          </div>

          <div className="flex-grow-1 d-flex gap-2">
            <input
              type="text"
              className="form-control"
              placeholder="Search by Event Title or Location..."
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setPage(1);
              }}
            />
            <button className="btn btn-primary px-4" onClick={handleSearch}>
              Search
            </button>
          </div>

          <button className="btn btn-outline-danger" onClick={handleDelete}>
            Delete ({selectedIds.length})
          </button>
        </div>

        {/* LOADING */}
        {loading && (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status" />
            <p className="mt-2 text-muted">Loading events...</p>
          </div>
        )}

        {/* EVENT CARDS */}
        {!loading &&
          currentJobs.map((job) => {
            const count = appCounts[job.id] || {};

            const title =
              job?.title || job?.event_title || job?.eventTitle || "Untitled Event";
            const location =
              job?.location || job?.venue || job?.event_location || "Location N/A";

            const rawStatus = job?.status || job?.eventStatus || job?.event_status || "";
            const isOpen =
              String(rawStatus).toLowerCase() === "open" ||
              job?.isActive === true ||
              job?.is_active === true;
            const statusLabel = isOpen ? "Open" : "Closed";

            const eventDate =
              job?.eventdate ||
              job?.event_date ||
              job?.eventDate ||
              job?.startDate ||
              job?.start_date ||
              job?.createdAt ||
              job?.created_at ||
              null;

            const students = count.students ?? getField(job, "students", "student_count", "studentCount", "totalStudents");
            const employees = count.employees ?? getField(job, "employees", "employee_count", "employeeCount", "totalEmployees");
            const representatives = count.representatives ?? getField(job, "representatives", "representative_count", "representativeCount", "totalRepresentatives");

            return (
              <div key={job.id} className="card mb-3 shadow-sm border-0">
                <div className="card-body">
                  {/* TOP ROW */}
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="d-flex align-items-start gap-2">
                      <input
                        type="checkbox"
                        style={{ transform: "scale(0.8)", accentColor: "#0d6efd" }}
                        className="form-check-input mt-1"
                        checked={selectedIds.includes(job.id)}
                        onChange={() => handleToggleSelect(job.id)}
                      />
                      <div>
                        <h6 className="mb-1">{title}</h6>
                        <small
                          className="text-muted"
                          style={{ display: "flex", alignItems: "center", gap: "4px" }}
                        >
                          <TfiLocationPin />
                          {location}
                        </small>
                      </div>
                    </div>

                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => handlePreview(job)}
                    >
                      Event Preview
                    </button>
                  </div>

                  {/* STATS ROW */}
                  <div className="d-flex flex-wrap justify-content-center gap-4 my-2">
                    <button
                      className="btn btn-sm btn-outline-primary d-flex flex-column align-items-center"
                      onClick={() => handleViewResponses(job)}
                    >
                      <span className="fw-semibold">{count.total || 0}</span>
                      <span className="small">Applications</span>
                    </button>

                    <button className="btn btn-sm btn-outline-primary d-flex flex-column align-items-center">
                      <span className="fw-semibold">{students}</span>
                      <span className="small">Students</span>
                    </button>

                    <button className="btn btn-sm btn-outline-success d-flex flex-column align-items-center">
                      <span className="fw-semibold">{employees}</span>
                      <span className="small">Employees</span>
                    </button>

                    <button className="btn btn-sm btn-outline-danger d-flex flex-column align-items-center">
                      <span className="fw-semibold">{representatives}</span>
                      <span className="small">Representatives</span>
                    </button>
                  </div>

                  {/* FOOTER ROW */}
                  <div className="d-flex flex-column align-items-end mt-0 small">
                    <span
                      className={`badge ${isOpen ? "bg-success" : "bg-danger"}`}
                      style={{ fontSize: "13px", marginBottom: "6px" }}
                    >
                      {statusLabel}
                    </span>
                    <span className="text-muted">
                      Posted:{" "}
                      <span className="text-warning fw-bold">
                        {formatDate(eventDate)}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            );
          })}

        {/* EMPTY STATE */}
        {!loading && currentJobs.length === 0 && (
          <div className="text-center py-5">
            <h6 className="text-muted">No events found</h6>
            <p className="text-muted small">Try adjusting your search filters</p>
            <button className="btn btn-success mt-3" onClick={handleAddJob}>
              <i className="bi bi-plus-circle me-2"></i>
              Post Your First Event
            </button>
          </div>
        )}

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-4">
            <small className="text-muted">
              Showing {(page - 1) * pageSize + 1} –{" "}
              {Math.min(page * pageSize, filteredJobs.length)} of {filteredJobs.length}
            </small>
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <small className="text-muted">
                Page {page} of {Math.max(1, totalPages)}
              </small>
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default KnTotalApplication;